/* global runtime */
// Hjem-modellen, frister og offentlige avgifter.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const splitOpenItemsByDueDate = (items, amountFor, dueDateFor, today) =>
  items.reduce((totals, item) => {
    const amount = Math.round(amountFor(item));
    const dueDate = dashboardDate(dueDateFor(item));
    if (dueDate && dueDate < today) {
      totals.overdue += amount;
    } else {
      totals.current += amount;
    }
    return totals;
  }, { current: 0, overdue: 0 });

const dashboardImportedOpenItemAmount = (item) =>
  num(item?.remainingAmount ?? item?.outstanding ?? item?.amountGross ?? item?.amount);

const dashboardImportedOutstandingSplit = (state, partyStatsKey, openItemsKey, today = new Date()) => {
  const statsEntries = safeArray(state.accountingImportPartyStats?.[partyStatsKey]);
  const originalStatsTotal = statsEntries
    .reduce((sum, entry) => sum + Math.max(0, Math.round(num(entry?.outstanding))), 0);
  const openItems = safeArray(state.accountingImportOpenItems?.[openItemsKey])
    .map((item) => accountingImportOpenItemModel().decorate?.(state, item) || item);
  const settledTotal = openItems.reduce((sum, item) =>
    sum + Math.max(0, num(item.originalRemainingAmount) - Math.max(0, num(item.remainingAmount))), 0);
  const statsTotal = Math.max(0, originalStatsTotal - settledTotal);
  const openItemSplit = splitOpenItemsByDueDate(
    openItems,
    dashboardImportedOpenItemAmount,
    (item) => item.dueDate || item.date,
    today,
  );
  const detailCurrent = Math.max(0, openItemSplit.current);
  const detailOverdue = Math.max(0, openItemSplit.overdue);
  const detailTotal = detailCurrent + detailOverdue;
  if (!statsEntries.length) {
    return { current: Math.round(detailCurrent), overdue: Math.round(detailOverdue) };
  }
  if (statsTotal <= 0 || detailTotal <= 0) {
    return { current: Math.round(statsTotal), overdue: 0 };
  }
  if (detailTotal > statsTotal) {
    const overdue = Math.round(statsTotal * (detailOverdue / detailTotal));
    return { current: statsTotal - overdue, overdue };
  }
  const overdue = Math.min(statsTotal, Math.round(detailOverdue));
  return {
    current: statsTotal - overdue,
    overdue,
  };
};

const statusFromDueDate = (submitted, dueDate, today) => {
  if (submitted) return 'ok';
  const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / 86400000);
  return daysUntilDue >= 0 && daysUntilDue <= 30 ? 'snart' : 'ikke';
};

const formatDashboardDeadlineDate = (date) =>
  date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });

const getDashboardVatDeadline = (year, state, today) => {
  const vatPeriod = window.PlummeVatPeriodHistory?.forDate?.(state.settings, `${year}-01-01`) || state.settings?.vatPeriod || 'bimonthly';
  if (!state.settings?.vatRegistered) return null;
  if (vatPeriod !== 'bimonthly') {
    return { key: 'mvaSubmitted', date: new Date(year + 1, 2, 10), label: 'MVA-melding' };
  }
  const terms = [
    { key: 'term-1', date: new Date(year, 3, 10), label: 'MVA-melding (1. termin)' },
    { key: 'term-2', date: new Date(year, 5, 10), label: 'MVA-melding (2. termin)' },
    { key: 'term-3', date: new Date(year, 7, 31), label: 'MVA-melding (3. termin)' },
    { key: 'term-4', date: new Date(year, 9, 10), label: 'MVA-melding (4. termin)' },
    { key: 'term-5', date: new Date(year, 11, 10), label: 'MVA-melding (5. termin)' },
    { key: 'term-6', date: new Date(year + 1, 1, 10), label: 'MVA-melding (6. termin)' },
  ];
  return terms.find((term) => term.date >= today) || terms[terms.length - 1];
};

const deriveDashboardDeadlines = (state, year, today) => {
  const deadlineStatus = state.deadlinesByYear?.[String(year)] || {};
  const entries = [];
  const vatDeadline = getDashboardVatDeadline(year, state, today);
  if (vatDeadline) {
    const submitted = vatDeadline.key.startsWith('term-')
      ? Boolean(deadlineStatus.mvaTerms?.[vatDeadline.key])
      : Boolean(deadlineStatus.mvaSubmitted);
    entries.push({
      id: vatDeadline.key,
      name: vatDeadline.label,
      date: formatDashboardDeadlineDate(vatDeadline.date),
      dueDate: vatDeadline.date.toISOString(),
      status: statusFromDueDate(submitted, vatDeadline.date, today),
    });
  }
  // Skattemelding, arsregnskap og aksjonaerregisteroppgaven kommer fra den delte
  // katalogen i annualDeadlines.js. Den avgjor bade hvem plikten gjelder — via
  // den delte selskapsklassifiseringen, ikke et lokalt `AS|ASA`-regex — og om
  // den er gjort opp, inkludert nar den ble levert utenfor Plumme.
  (globalThis.PlummeAnnualDeadlines?.annualDeadlinesForIncomeYear?.(state, year) || []).forEach((entry) => {
    entries.push({
      id: entry.id,
      name: entry.name,
      date: formatDashboardDeadlineDate(entry.dueDate),
      dueDate: entry.dueDate.toISOString(),
      status: statusFromDueDate(entry.submitted, entry.dueDate, today),
      // Plumme har ingen innsendingsmodul for denne plikten, og kjenner ikke
      // unntakene den har. Raden er en paaminnelse, ikke en konklusjon.
      qualified: entry.qualified || undefined,
      ruleId: entry.ruleId,
    });
  });
  return entries.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
};

const percentChange = (current, previous) =>
  previous ? Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10 : null;

const positiveAmount = (value) => Math.max(0, num(value));
const displayAmount = (value) => Math.round(value * 100) / 100;
const payrollRunPayableEmployerContribution = (run = {}) => {
  const postingRows = safeArray(run.payrollPostingRows);
  if (postingRows.length) {
    return Math.max(0, postingRows
      .filter((row) => String(row?.accountCode || '') === '2770')
      .reduce((sum, row) => sum + num(row.credit) - num(row.debit), 0));
  }
  return safeArray(run.lines).reduce((sum, line) => sum + positiveAmount(line.employerContribution), 0);
};
const isCustomNorwegianReportingProfile = (settings) =>
  settings?.country === 'NO' && /^(as|asa|nuf|samvirke|sa|ans|da|ks)$/i.test(settings?.companyTypeCode || '');
const shouldShowTaxBuffer = (settings) =>
  !isCustomNorwegianReportingProfile(settings) &&
  !/^(as|asa|nuf)$/i.test(settings?.companyTypeCode || '');
const dashboardPayrollLiabilities = (payrollRuns) =>
  safeArray(payrollRuns)
    .filter((run) => run.status !== 'draft' && run.status !== 'reversed')
    .reduce((totals, run) => {
      const runTotals = {
        employeeTax: safeArray(run.lines).reduce((sum, line) => sum + positiveAmount(line.taxDeduction), 0),
        employerContribution: payrollRunPayableEmployerContribution(run),
      };
      return {
        employeeTax: totals.employeeTax + (run.paymentStatus?.taxDeductionPaidAt ? 0 : runTotals.employeeTax),
        employerContribution: totals.employerContribution + (run.paymentStatus?.employerContributionPaidAt ? 0 : runTotals.employerContribution),
      };
    }, { employeeTax: 0, employerContribution: 0 });
const dashboardPublicFeeRows = ({ state, year, vatDue, result }) => {
  const payrollLiabilities = dashboardPayrollLiabilities(state.payrollRuns);
  const taxPrepaymentPaid = safeArray(state.taxPrepaymentsByYear?.[String(year)])
    .reduce((sum, entry) => sum + positiveAmount(entry.amount), 0);
  const suggestedTaxBuffer = shouldShowTaxBuffer(state.settings) ? positiveAmount(result) * 0.4 : 0;
  const rows = [
    { id: 'employerContribution', label: 'AGA', amount: payrollLiabilities.employerContribution },
    { id: 'employeeTax', label: 'Forskuddstrekk ansatte', amount: payrollLiabilities.employeeTax },
    { id: 'vat', label: 'MVA', amount: state.settings?.vatRegistered ? positiveAmount(vatDue) : 0 },
    { id: 'taxBuffer', label: 'Buffer til skatt', amount: Math.max(suggestedTaxBuffer - taxPrepaymentPaid, 0) },
  ];
  return rows
    .map((row) => ({ ...row, amount: displayAmount(row.amount) }))
    .filter((row) => row.amount > 0.005);
};

const buildHomeDashboardModel = () => {
  if (!runtime.appState || (!runtime.configured && !runtime.user)) return null;
  const state = mergedRuntimeState();
  const year = Number(state.activeYear) || new Date().getFullYear();
  const today = new Date();
  const chart = addImportedSalesHistoryToDashboardChart(deriveReportChart(state, year, { includeFinance: true }), state, year);
  const previousChart = addImportedSalesHistoryToDashboardChart(deriveReportChart(state, year - 1, { includeFinance: true }), state, year - 1);
  const report = deriveReportResult(state, year);
  const previousReport = deriveReportResult(state, year - 1);
  const importedSalesRevenue = importedSalesHistoryFallbackRevenue(state, year);
  const previousImportedSalesRevenue = importedSalesHistoryFallbackRevenue(state, year - 1);
  const income = report.totaleInntekter + importedSalesRevenue;
  const costs = report.totaleKostnader;
  const previousIncome = previousReport.totaleInntekter + previousImportedSalesRevenue;
  const previousCosts = previousReport.totaleKostnader;
  const openInvoiceSplit = splitOpenItemsByDueDate(
    dashboardOpenInvoices(state),
    (invoice) => dashboardInvoicePaymentSummary(state, invoice).remainingAmount,
    (invoice) => invoice.dueDate || invoice.validUntil,
    today,
  );
  const openPurchaseSplit = splitOpenItemsByDueDate(
    dashboardOpenSupplierInvoices(state),
    (expense) => dashboardSupplierInvoicePaymentSummary(state, expense).remainingAmount,
    (expense) => expense.supplierInvoiceDueDate || expense.dueDate || expense.date,
    today,
  );
  const importedSalesSplit = dashboardImportedOutstandingSplit(state, 'customers', 'customerInvoices', today);
  const importedPurchaseSplit = dashboardImportedOutstandingSplit(state, 'suppliers', 'supplierInvoices', today);
  const salesCurrent = openInvoiceSplit.current + importedSalesSplit.current;
  const salesOverdue = openInvoiceSplit.overdue + importedSalesSplit.overdue;
  const purchasesCurrent = openPurchaseSplit.current + importedPurchaseSplit.current;
  const purchasesOverdue = openPurchaseSplit.overdue + importedPurchaseSplit.overdue;
  const vat = deriveVat(state, year);
  const bankDocumentedPostingIds = window.PlummePostingDocumentation.bankDocumentedPostingIds(state);
  const missingAttachmentExpenses = safeArray(state.expenses).filter((expense) =>
    !window.PlummePostingDocumentation.isBankDocumentedPosting(expense, bankDocumentedPostingIds) &&
    !safeArray(expense.manualPostingLines).some((line) => [line.fromAccount, line.toAccount].some((code) =>
      Number(code) >= 2000 && Number(code) <= 2009)) &&
    expense.status !== 'draft' &&
    expense.status !== 'annulled' &&
    !attachmentMarkedLost(expense) &&
    !isMileageExpense(expense) &&
    !expense.attachmentId &&
    !expense.fileName &&
    !expense.receiptFile &&
    !safeArray(expense.attachments).length,
  );
  const missingAttachmentCount = missingAttachmentExpenses.length;
  const missingAttachmentPurchases = missingAttachmentExpenses.filter((expense) => !isFreePostingExpense(expense));
  const missingAttachmentPurchaseCount = missingAttachmentPurchases.length;
  const missingAttachmentPurchaseNet = Math.round(missingAttachmentPurchases.reduce((sum, expense) => sum + Math.max(0, num(expense.amountNet)), 0));
  // Belopet bak de manglende bilagene, ikke bare antallet. Uten det kan radgiveren si
  // at fire kjop mangler bilag, men ikke hva det faktisk star om i fradrag og mva.
  const missingAttachmentNet = Math.round(missingAttachmentExpenses.reduce((sum, expense) => sum + Math.max(0, num(expense.amountNet)), 0));
  const missingAttachmentVat = Math.round(missingAttachmentExpenses.reduce((sum, expense) =>
    sum + Math.max(0, num(expense.amountNet)) * (num(expense.vatRate) / 100), 0));
  const draftInvoices = safeArray(state.invoices).filter((invoice) =>
    invoice.kind !== 'credit' &&
    invoice.kind !== 'offer' &&
    invoice.status === 'draft'
  ).length;
  const unsentInvoices = safeArray(state.invoices).filter((invoice) =>
    invoice.kind !== 'credit' &&
    invoice.kind !== 'offer' &&
    invoice.status !== 'draft' &&
    !invoice.sentAt &&
    !invoice.paidAt,
  ).length;
  const overdueInvoices = dashboardOpenInvoices(state).filter((invoice) => {
    const dueDate = dashboardDate(invoice.dueDate || invoice.validUntil);
    return invoice.sentAt && dueDate && dueDate < today;
  }).length;
  const result = report.resultat + importedSalesRevenue;
  const previousResult = previousReport.resultat + previousImportedSalesRevenue;
  const payables = purchasesCurrent + purchasesOverdue;
  const receivables = salesCurrent + salesOverdue;
  const liquidityRatio = payables > 0 ? receivables / payables : null;
  const profitability = income > 0 ? (result / income) * 100 : null;
  const equityProxy = result - payables;
  const assetsProxy = Math.max(receivables + Math.max(result, 0), 0);
  const solidity = assetsProxy > 0 ? (equityProxy / assetsProxy) * 100 : null;
  const followupGroups = buildDashboardFollowupGroups({ state, year, today });

  return {
    year,
    chart,
    summary: {
      income,
      costs,
      result,
      previousIncome,
      previousCosts,
      previousResult,
    },
    overview: {
      salesCurrent,
      salesOverdue,
      purchasesCurrent,
      purchasesOverdue,
      importedSalesCurrent: importedSalesSplit.current,
      importedSalesOverdue: importedSalesSplit.overdue,
      importedPurchasesCurrent: importedPurchaseSplit.current,
      importedPurchasesOverdue: importedPurchaseSplit.overdue,
    },
    deadlines: deriveDashboardDeadlines(state, year, today),
    followup: {
      groups: followupGroups,
      draftInvoices,
      unsentInvoices,
      overdueInvoices,
      missingAttachmentCount,
      missingAttachmentPurchaseCount,
      missingAttachmentPurchaseNet,
      missingAttachmentNet,
      missingAttachmentVat,
      missingAttachmentItems: missingAttachmentExpenses.map((expense) => ({
        id: expense.id,
        kind: isFreePostingExpense(expense) ? 'posting' : 'purchase',
        label: String(expense.supplierName || expense.description || expense.title || '').trim(),
        date: expense.date || '',
        amountNet: Math.abs(num(expense.amountNet)),
      })),
      openPurchaseCount: dashboardOpenSupplierInvoices(state).length,
    },
    publicFees: {
      vatDue: vat.skyldig,
      taxBuffer: result > 0 ? Math.round(result * 0.4) : 0,
      rows: dashboardPublicFeeRows({ state, year, vatDue: vat.skyldig, result }),
    },
    analysis: {
      incomeYoy: percentChange(income, previousIncome),
      costsYoy: percentChange(costs, previousCosts),
      resultYoy: percentChange(result, previousResult),
      liquidityRatio,
      profitability,
      solidity,
    },
  };
};
// Hjem-modellen bygger arsrapporten for i ar og i fjor, med manedsfordeling — det er
// 26 gjennomganger av alle bilag. Den kalles fra bade hjem, radgiver, utfallsmalingen
// og skatteradene, sa den ma bygges en gang per state, ikke en gang per kall.
